import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LifeBuoy,
  X,
  ArrowLeft,
  Loader2,
  Send,
  Check,
  ChevronDown,
  Settings,
  ShieldCheck,
  Users,
  FileEdit,
  Lightbulb,
  HelpCircle,
  Clock,
  Minus,
  Paperclip,
  Image as ImageIcon,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trackEvent } from "@/hooks/useEventTracking";
import { PrivacyNotice } from "@/components/PrivacyNotice";

type Category = "tech" | "verification" | "team" | "data_change" | "idea" | "other";

const CATEGORIES: {
  id: Category;
  label: string;
  Icon: LucideIcon;
  urgentEligible?: boolean;
}[] = [
  { id: "tech", label: "Problem techniczny w panelu", Icon: Settings },
  { id: "verification", label: "Weryfikacja organizacji", Icon: ShieldCheck },
  { id: "team", label: "Zarządzanie zespołem", Icon: Users },
  { id: "data_change", label: "Problem z edycją profilu", Icon: FileEdit, urgentEligible: true },
  { id: "idea", label: "Sugestia / feedback", Icon: Lightbulb },
  { id: "other", label: "Inny problem", Icon: HelpCircle },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Które zmiany zapisują się od razu?",
    a: "Od razu aktualizują się potrzeby, wolontariat, kontakt (telefon, e-mail, www) oraz opis schroniska. Akceptacji administratora wymagają zmiany nazwy i lokalizacji schroniska (adres, miasto, województwo). Weryfikacja zwykle trwa do 24-48h.",
  },
  {
    q: "Jak długo trwa weryfikacja organizacji?",
    a: "Weryfikacja zwykle trwa do 24-48h. W niektórych przypadkach możemy skontaktować się mailowo lub z oficjalnym profilem organizacji w celu potwierdzenia zgłoszenia.",
  },
  {
    q: "Jak zaprosić osobę do zespołu?",
    a: "Zespół schroniska może liczyć maksymalnie 3 osoby - opiekun profilu + 2 dodatkowych członków. Zaproszenia wysyła opiekun profilu z zakładki „Zespół schroniska”, a zaproszona osoba dostaje link aktywacyjny e-mailem.",
  },
  {
    q: "Dlaczego nie widzę swojego schroniska?",
    a: "Nowe profile pojawiają się na mapie po weryfikacji administratora. Zwykle trwa to do 24-48h.",
  },
  {
    q: "Jak oznaczyć potrzebę jako pilną?",
    a: "W edycji profilu wpisz pilne potrzeby w polu „Pilne potrzeby”. Wygasają automatycznie po 30 dniach. Pilne potrzeby warto oznaczać tylko w naprawdę ważnych sytuacjach - np. przy braku karmy, leczeniu zwierząt lub nagłej zbiórce.",
  },
];

const STORAGE_KEY = "panel_help_widget_minimized_v1";
const MINIMIZE_DAYS = 7;

const isMinimizedFresh = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < MINIMIZE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const PanelHelpWidget = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Ukryj widget na stronach z sekcjami niebezpiecznymi (np. ustawienia konta),
  // żeby nie konkurował wizualnie z czerwonym CTA.
  const HIDDEN_ROUTES = ["/panel/konto"];
  const shouldHide = HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p));

  const [minimized, setMinimized] = useState<boolean>(true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"home" | "form" | "done">("home");
  const [category, setCategory] = useState<Category | null>(null);
  const [message, setMessage] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [attachment, setAttachment] = useState<{ file: File; previewUrl: string } | null>(null);

  const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
  const ATTACHMENT_ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // pozwala wybrać ten sam plik ponownie
    if (!file) return;
    if (!ATTACHMENT_ALLOWED.includes(file.type)) {
      toast.error("Tylko obrazy: JPG, PNG, WEBP, HEIC, GIF");
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error("Plik jest za duży (max 5 MB)");
      return;
    }
    if (attachment) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment({ file, previewUrl: URL.createObjectURL(file) });
  };

  const removeAttachment = () => {
    if (attachment) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        resolve(res.includes(",") ? res.split(",")[1] : res);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  // Auto-kontekst: shelter + rola użytkownika
  const [ctx, setCtx] = useState<{
    shelter_id: string | null;
    shelter_name: string | null;
    role: "owner" | "member" | null;
    org_verified: boolean | null;
  }>({ shelter_id: null, shelter_name: null, role: null, org_verified: null });

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data: ownerRow } = await supabase
        .from("shelter_owners")
        .select("shelter_id, role")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || !ownerRow) return;
      const { data: shelter } = await supabase
        .from("shelters")
        .select("id, name, organization_verified")
        .eq("id", ownerRow.shelter_id)
        .maybeSingle();
      if (cancelled) return;
      setCtx({
        shelter_id: shelter?.id ?? null,
        shelter_name: shelter?.name ?? null,
        role: (ownerRow.role as "owner" | "member") ?? null,
        org_verified: shelter?.organization_verified ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    trackEvent("Panel wsparcie - otwarcie", { path: location.pathname });
  };

  // Nasłuch globalnego eventu "panel-help:report-bug" (z ErrorBoundary lub
  // z toastów łapiących błędy runtime). Otwiera widget od razu w trybie
  // formularza z prefillem kategorii "tech" i opisem błędu.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string; stack?: string | null; source?: string; path?: string }>).detail || {};
      setOpen(true);
      setMinimized(false);
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      setCategory("tech");
      setStep("form");
      const lines = [
        "Wystąpił błąd w panelu. Dołączam zrzut ekranu (jeśli to możliwe).",
        "",
        "--- Szczegóły techniczne (auto) ---",
        detail.message ? `Błąd: ${detail.message}` : null,
        detail.source ? `Źródło: ${detail.source}` : null,
        detail.path ? `Strona: ${detail.path}` : null,
        detail.stack ? `Stack:\n${String(detail.stack).slice(0, 1500)}` : null,
      ].filter(Boolean).join("\n");
      setMessage(lines);
      trackEvent("Panel wsparcie - auto blad", { source: detail.source ?? "unknown" });
    };
    window.addEventListener("panel-help:report-bug", handler as EventListener);
    return () => window.removeEventListener("panel-help:report-bug", handler as EventListener);
  }, []);

  const handleMinimize = () => {
    setOpen(false);
    setMinimized(true);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
    resetForm();
  };

  const resetForm = () => {
    setStep("home");
    setCategory(null);
    setMessage("");
    setUrgent(false);
    setOpenFaq(null);
    removeAttachment();
  };

  const pickCategory = (cat: Category) => {
    setCategory(cat);
    setStep("form");
    trackEvent("Panel wsparcie - kategoria", { category: cat });
  };

  const goBack = () => {
    if (step === "form") {
      setStep("home");
      setCategory(null);
      setUrgent(false);
    }
  };

  const categoryLabel = useMemo(
    () => CATEGORIES.find((c) => c.id === category)?.label ?? "",
    [category],
  );

  const urgentEligible = useMemo(
    () => CATEGORIES.find((c) => c.id === category)?.urgentEligible ?? false,
    [category],
  );

  const handleSubmit = async () => {
    if (!category) return;
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      toast.error("Wiadomość musi mieć co najmniej 5 znaków");
      return;
    }
    setSubmitting(true);
    try {
      // Doklejamy ukryty kontekst dla admina
      const contextLines = [
        "--- Kontekst (auto) ---",
        `Konto: ${user?.email ?? "?"}`,
        `Rola: ${ctx.role ?? "?"}`,
        ctx.shelter_name
          ? `Schronisko: ${ctx.shelter_name} (${ctx.shelter_id ?? "?"})`
          : "Schronisko: (brak powiązania)",
        `Weryfikacja organizacji: ${ctx.org_verified === null ? "?" : ctx.org_verified ? "tak" : "nie"}`,
        `Ścieżka: ${location.pathname}`,
        urgent ? "PILNE: tak" : null,
      ]
        .filter(Boolean)
        .join("\n");

      // Limit DB: feedback_messages.message ma CHECK <= 4000 znaków.
      // Twardy cap z marginesem na ewentualne zmiany kontekstu w przyszłości.
      const MAX_LEN = 3900;
      const combined = `${trimmed}\n\n${contextLines}`;
      const finalMessage = combined.length > MAX_LEN
        ? combined.slice(0, MAX_LEN - 20) + "\n...[obcięto]"
        : combined;

      let attachmentPayload: { name: string; mime_type: string; base64: string } | null = null;
      if (attachment) {
        try {
          const b64 = await fileToBase64(attachment.file);
          attachmentPayload = {
            name: attachment.file.name.slice(0, 120),
            mime_type: attachment.file.type,
            base64: b64,
          };
        } catch (e) {
          console.error("[PanelHelpWidget] file read error", e);
          toast.error("Nie udało się odczytać pliku");
          setSubmitting(false);
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke("submit-feedback", {
        body: {
          category: "other",
          subcategory: `Panel: ${categoryLabel}${urgent ? " (PILNE)" : ""}`,
          message: finalMessage,
          email: user?.email ?? null,
          shelter_name: ctx.shelter_name,
          shelter_id: ctx.shelter_id,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          attachment: attachmentPayload,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      setStep("done");
      trackEvent("Panel wsparcie - wyslane", {
        category,
        urgent,
        has_shelter: !!ctx.shelter_id,
      });
    } catch (err: any) {
      toast.error(err?.message || "Nie udało się wysłać wiadomości");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== RENDER =====

  if (shouldHide) return null;

  if (minimized && !open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Otwórz wsparcie"
        className="fixed bottom-5 right-4 sm:bottom-6 sm:right-5 z-[2000] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground shadow-glow-soft hover:shadow-glow transition-all hover:scale-105 flex items-center justify-center"
      >
        <LifeBuoy className="w-5 h-5" />
      </button>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Otwórz wsparcie"
        className="fixed bottom-5 right-4 sm:bottom-6 sm:right-5 z-[2000] h-11 sm:h-12 pl-3 pr-4 rounded-full bg-primary text-primary-foreground shadow-glow-soft hover:shadow-glow transition-all hover:scale-105 flex items-center gap-2 font-semibold text-sm animate-fade-in"
      >
        <LifeBuoy className="w-5 h-5" />
        Pomoc
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[2100] w-[calc(100vw-2rem)] sm:w-[26rem] max-h-[calc(100vh-2rem)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          {step === "form" && (
            <button
              type="button"
              onClick={goBack}
              aria-label="Wstecz"
              className="hover:bg-white/10 rounded-full p-1"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <LifeBuoy className="w-4 h-4" />
          <span className="font-semibold text-sm">
            {step === "done" ? "Wysłano" : "Wsparcie zespołu"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleMinimize}
            aria-label="Ukryj na 7 dni"
            title="Ukryj na 7 dni"
            className="hover:bg-white/10 rounded-full p-1"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setMinimized(true); }}
            aria-label="Zamknij"
            className="hover:bg-white/10 rounded-full p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {step === "home" && (
          <div className="space-y-4">
            {/* Mini-FAQ */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Najczęstsze pytania
              </p>
              <div className="space-y-1.5">
                {FAQ.map((item, i) => {
                  const isOpen = openFaq === i;
                  return (
                    <div
                      key={i}
                      className={`border rounded-lg overflow-hidden transition-colors ${
                        isOpen ? "border-primary/30 bg-primary/5" : "border-border bg-transparent"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : i)}
                        aria-expanded={isOpen}
                        className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors"
                      >
                        <span className="text-xs font-medium leading-snug">{item.q}</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${
                            isOpen ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-all duration-200 ease-out ${
                          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-3 pb-3 pt-0 text-xs text-muted-foreground leading-relaxed">
                            {item.a}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Kategorie */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                Potrzebujesz pomocy?
              </p>
              <div className="space-y-1.5">
                {CATEGORIES.map((c) => {
                  const Icon = c.Icon;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pickCategory(c.id)}
                      className="w-full text-left p-2.5 rounded-lg border border-border hover:border-primary hover:bg-muted/40 transition-all flex items-center gap-2.5 group"
                    >
                      <span
                        className="w-7 h-7 rounded-md bg-muted/60 group-hover:bg-primary/10 group-hover:text-primary text-muted-foreground flex items-center justify-center shrink-0 transition-colors"
                        aria-hidden
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </span>
                      <span className="font-medium text-xs">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === "form" && category && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Temat: <span className="font-medium text-foreground">{categoryLabel}</span>
            </div>

            {/* Auto-kontekst (widoczny info-pasek) */}
            <div className="rounded-lg bg-muted/50 border border-border p-2.5 text-xs space-y-1.5">
              <p className="text-muted-foreground">
                Automatycznie dołączymy informacje o Twoim profilu:
              </p>
              {ctx.shelter_name ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{ctx.shelter_name}</span>
                  {user?.email && (
                    <span className="text-muted-foreground">{user.email}</span>
                  )}
                </div>
              ) : (
                <p className="text-foreground">
                  {user?.email ?? "Twoje konto"} <span className="text-muted-foreground">(brak powiązanego schroniska)</span>
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="phw-message" className="text-xs">
                Wiadomość *
              </Label>
              <Textarea
                id="phw-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Napisz krótko, z czym potrzebujesz pomocy"
                rows={4}
                className="text-base sm:text-sm resize-none"
              />
            </div>

            {urgentEligible && (
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={urgent}
                  onChange={(e) => setUrgent(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-muted-foreground">
                  <span className="font-medium text-foreground">Sprawa pilna</span> - dane
                  schroniska są błędne i wprowadzają adoptujących w błąd
                </span>
              </label>
            )}

            {/* Załącznik (1 obrazek) */}
            <div>
              {attachment ? (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-2">
                  <div className="w-10 h-10 rounded-md overflow-hidden bg-background border border-border flex items-center justify-center shrink-0">
                    {attachment.file.type.startsWith("image/") && !attachment.file.type.includes("heic") ? (
                      <img
                        src={attachment.previewUrl}
                        alt="Podgląd załącznika"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{attachment.file.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {(attachment.file.size / 1024).toFixed(0)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    aria-label="Usuń załącznik"
                    className="w-6 h-6 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors w-fit">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors">
                    <Paperclip className="w-3.5 h-3.5" />
                  </span>
                  <span>Dodaj zrzut ekranu (opcjonalnie)</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
                    onChange={handleAttachmentChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full"
              size="sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wysyłanie…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Wyślij zgłoszenie
                </>
              )}
            </Button>

            <PrivacyNotice />

            <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/80">
              <Clock className="w-3 h-3" strokeWidth={1.75} />
              Odpowiadamy zwykle w ciągu 1-2 dni roboczych.
            </p>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-3 text-center py-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-sm">Zgłoszenie zostało wysłane</p>
              <p className="text-xs text-muted-foreground">
                Dziękujemy! Odpowiemy możliwie szybko.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={resetForm}>
              Powrót do pomocy
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelHelpWidget;
